import * as React from 'react';
import {useContext, useState, useEffect} from 'react'
import classnames from 'classnames'
import {Link} from 'react-router-dom'
import {WorkflowNotificationList} from "../../../services/notifications/WorkflowNotificationList";
import {useObserver} from "mobx-react"
import {Collapse} from "reactstrap";
import {AppNavigatorContext} from "../navigator/AppNavigator";
import {useNavPreferences} from "../../../uiMigration/navPreferences";
import RegisterPortal from '../../../components/portals/RegisterPortal';
import { PAGE_FILTERS, GLOBAL_FILTER } from '../navigator/navigatorPortalNames';
import "./standard-layout.css";
import { renderNavSections, NavItem } from "./NavRenderingUtils";
import PageHeaderLayout from './PageHeaderLayout';
import UsePageTitle from './UsePageTitle';
import PageTitle from '../../../components/typography/PageTitle';
import { HelpIcon } from '../../../components';

export function StandardLayout({
     children,
     className
}) {

  const [mobileTabsOpen, setMobileTabsOpen] = useState(false);
  const toggleMobileTabs = () => setMobileTabsOpen(!mobileTabsOpen)
  const prefs = useObserver(() => useNavPreferences());
  const navigationContext = useContext(AppNavigatorContext);


  function renderHeaderLinks() {
    return (
      <span className="header-links">
        {!prefs.hideWelcomeText && (
          <span className="welcome-text">Welcome, {prefs.userFirstName}</span>
        )}
        <ul className="nav">
          {!prefs.condensedAccountLinks && <br />}
          {!prefs.hideWorkflowNotifications && prefs.hideNotificationsSettings  && (
            <li className="nav-item d-flex align-items-center" ng-if="!hideWorkflow2">
              <WorkflowNotificationList />
            </li>
          )}


          {!prefs.hideEmailAlerts && (
            <li className="nav-item d-flex align-items-center">
              <a
                className="nav-link"
                href="#/emailAlerts/manage"
              >
                <span className="mr-2">My Alerts</span>
                <span
                  className="fas fa-envelope"
                />
              </a>
            </li>
          )}

          {!prefs.hideMyAccount && (
            <li className="nav-item d-flex align-items-center">
              <a className="nav-link" href="#/admin/v2/user">My Account</a>
            </li>
          )}

          {!prefs.hideLogout && (
            <span className="nav-item d-flex align-items-center">
              <a className="nav-link" href="#/user/logout" ng-hide="hideLogout">
                Logout
              </a>
            </span>
          )}
        </ul>
      </span>
    );

  }

  function renderTabs() {
    if (!navigationContext.navL1.length) {
      return null;
    }
    return <ul className="navbar-nav feature-nav">
      {
        navigationContext.navL1.map(({ path, title, active, exact }) => {
          return <li className={"nav-item p-0"} key={path}>
            <Link
              to={path}
              exact={exact}
              className={"nav-link p-3 text-nowrap " + (active ? "active" : "")}>{title}</Link>
          </li>
        })
      }
    </ul>
  }

  function renderSideBar({
    children
  }) {
    if (!navigationContext.navL2.length) {
      return (
        <div className="container-fluid clearfix px-3">
          <div className="row">
            <div className="col-12 card-spacer-top content-area-bottom-spacer">{children}</div>
          </div>
        </div>
      );
    } else {
      return <div className="container-fluid clearfix px-3">
        <div className="row">
          <div className="col-12 col-sm-2">
            <nav className="nav nav-pills nav-stacked sticky-top d-block card-spacer-padding-top">
              { renderNavSections(navigationContext.navL2) }
            </nav>
          </div>
          <div className="col-12 col-sm-10 card-spacer-top content-area-bottom-spacer">{children}</div>
        </div>
      </div>
    }
  }

  function renderPills() {
    if (!navigationContext.navL3.length) {
      return null
    } else {
      return <nav className="nav nav-pills d-inline-flex flex-column cursor-default card-spacer-bottom">
        <div className="d-flex flex-row">
          {
            navigationContext.navL3.map(({path, title, active, icon, exact}) => {
              return <NavItem path={path} key={path} active={active} exact={exact}>{title}</NavItem>
            })
          }
        </div>
      </nav>
    }
  }

  //Needed to handle modals that are rendered outside of the normal container.
  prefs.rebrandClass && window.document.body.classList.add(prefs.rebrandClass);

  useEffect(() => {
    //document class To handle tooltips/etc
    window.document.body.classList.add("standard");
    return () => window.document.body.classList.remove("standard");
  }, []);

  return (
    <div
      className={classnames(
        "main-container d-flex flex-column fill-height show-commands-container",
        className,
        prefs.rebrandClass
      )}
    >
      <div>
        <div className="main-header-top">
          {!prefs.condensedAccountLinks && (
            <div className="header clearfix py-0 border-0">
              <div className="container-fluid d-flex align-items-center">
                {!prefs.hideLogo && (
                  <span>
                    <Link to="/" className="logo" title="home">
                      <img
                        className="navbar-brand-logo"
                        src={prefs.logoUrl}
                        alt="logo"
                      />
                    </Link>
                  </span>
                )}
                {!prefs.condensedAccountLinks && (
                  <div className="top-menu logged-as ml-auto m-0 p-0 d-flex align-items-end">
                    {renderHeaderLinks()}
                  </div>
                )}
              </div>
            </div>
          )}
          <nav
            className={`main-navbar navbar navbar-expand-lg flex-wrap clearfix p-0 ${prefs.condensedAccountLinks ? "border-top-0" : ""}`}
            role="navigation"
          >
            {/* mobile/tablet tab collapse */}
            <button
              className="navbar-toggler ml-3 my-2"
              type="button"
              onClick={toggleMobileTabs}
            >
              <i className="fas fa-bars d-flex"></i>
            </button>
            <RegisterPortal id={GLOBAL_FILTER} tagProps={{className: "order-lg-1 ml-auto"}}/>
            <Collapse
              className="mb-2 mb-lg-0 order-lg-0"
              isOpen={mobileTabsOpen}
              navbar
            >
              {renderTabs()}
            </Collapse>
            {prefs.condensedAccountLinks && renderHeaderLinks()}
          </nav>
        </div>
      </div>

      <div className="flex-1">
        {renderSideBar(
          {
            children: (
                <React.Fragment>
                  <RegisterPortal id={PAGE_FILTERS} />
                  {/* When a page doesn't have filters */}
                  <div className="card-spacer-bottom">
                    <PageHeaderLayout />
                    {
                      !navigationContext.activeNode.hasPageTitleOverride && <UsePageTitle>
                        <div className="d-flex flex-row align-items-center">
                          <PageTitle>
                            {navigationContext.activeNode.title}
                          </PageTitle>
                            {navigationContext.activeNode.helpIconMessage ? <HelpIcon style={{textAlign: "left"}} message={navigationContext.activeNode.helpIconMessage}/> : null}
                        </div>
                      </UsePageTitle>
                    }
                  </div>
                  {renderPills()}
                  {children}
                </React.Fragment>
              ),
            }
          )
        }
      </div>
    </div>
  );
}
